import { QUESTIONS } from "./questions.js";
import { computeScore, deriveTopTwo, normaliseAnswer, computeGroupScores, CATEGORY_GROUPS } from "./scoring.js";
import { buildFeedback, overallBand, statusForScore } from "./feedback.js";
import { submitQuiz } from "./submit.js";

const SCREENS = ["hero", "quiz", "email", "results", "calendly", "confirmation"];

// Questions reordered into category groups for sequential display
const GROUPED_QUESTIONS = CATEGORY_GROUPS.flatMap(({ questionIndices }) =>
  questionIndices.map((idx) => ({ idx, question: QUESTIONS[idx] }))
);

// Category heading + position-within-category for each display index
const Q_META = CATEGORY_GROUPS.flatMap(({ label, questionIndices }) =>
  questionIndices.map((_, i) => ({ category: label, pos: i + 1, total: questionIndices.length }))
);

const state = {
  currentScreen: "quiz",
  currentQuestion: 0,
  answers: {},
  email: "",
  name: "",
  score: null,
  topTwo: [],
  perQuestion: [],
  feedback: [],
  groupScores: [],
  source: "organic",
};

let root;

function $(sel, ctx) { return (ctx || root).querySelector(sel); }
function $$(sel, ctx) { return (ctx || root).querySelectorAll(sel); }

const params = new URLSearchParams(window.location.search);
const DEV_MODE = params.has("dev");
const PREFILLED_UID = params.get("qzc") || params.get("uid");

function init() {
  root = document.querySelector(".airtable-maturity-assessment");
  if (!root) return;
  if (params.get("src") === "email") state.source = "email";
  const progress = $(".aq-progress");
  if (progress) progress.hidden = true;
  const qCount = $(".aq-hero-q-count");
  if (qCount) qCount.textContent = `${QUESTIONS.length} questions`;

  if (DEV_MODE) {
    $$("[data-screen]").forEach((el) => { el.hidden = false; });
    renderAllQuestions();
    return;
  }

  showScreen("hero");
}

// --- Screen transitions ---

function showScreen(name) {
  if (DEV_MODE) return;
  state.currentScreen = name;
  $$("[data-screen]").forEach((el) => {
    el.hidden = el.dataset.screen !== name;
  });
}

// --- Progress bar ---

function updateProgress() {
  const bar = $(".aq-progress-bar");
  if (!bar) return;
  const done = state.currentScreen !== "quiz"
    ? GROUPED_QUESTIONS.length
    : state.currentQuestion;
  const pct = (done / GROUPED_QUESTIONS.length) * 100;
  bar.style.width = pct + "%";

  const counter = $(".aq-progress-count");
  if (counter) {
    if (state.currentScreen !== "quiz") {
      counter.textContent = "";
    } else {
      const meta = Q_META[state.currentQuestion];
      counter.textContent = meta ? `${meta.category} · ${meta.pos} of ${meta.total}` : "";
    }
  }
}

// --- Scroll helper ---

function scrollToQuizTop() {
  const progress = $(".aq-progress");
  if (progress) progress.scrollIntoView({ behavior: "smooth", block: "start" });
}

// --- Dev mode: all questions ---

function renderAllQuestions() {
  const container = $("[data-screen='quiz'] .aq-question-area");
  if (!container) return;
  container.innerHTML = "";

  GROUPED_QUESTIONS.forEach(({ idx, question: q }, displayIdx) => {
    const meta = Q_META[displayIdx];
    const section = document.createElement("div");
    section.className = "aq-question";
    section.style.marginBottom = "3rem";
    section.style.paddingBottom = "2rem";
    section.style.borderBottom = "1px solid #e0dff0";

    const bodyCol = document.createElement("div");
    bodyCol.className = "aq-question-body";

    const label = document.createElement("p");
    label.style.cssText = "font-size:0.8rem;font-weight:600;text-transform:uppercase;letter-spacing:0.06em;color:#6D1E6F;margin:0 0 0.5rem";
    label.textContent = meta ? `${meta.category} · ${meta.pos} of ${meta.total}` : "";
    bodyCol.appendChild(label);

    const text = document.createElement("h2");
    text.className = "aq-question-text";
    text.textContent = q.text;
    bodyCol.appendChild(text);

    if (q.context) {
      const ctx = document.createElement("p");
      ctx.className = "aq-question-context";
      ctx.textContent = q.context;
      bodyCol.appendChild(ctx);
    }

    const optionsWrap = document.createElement("div");
    optionsWrap.className = "aq-options";
    Object.keys(q.answerAdvice).forEach((opt) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "aq-option";
      btn.textContent = opt;
      btn.style.cursor = "default";
      optionsWrap.appendChild(btn);
    });
    bodyCol.appendChild(optionsWrap);

    const adviceSection = document.createElement("div");
    adviceSection.style.cssText = "margin-top:1.5rem;display:flex;flex-direction:column;gap:1rem";
    Object.entries(q.answerAdvice).forEach(([opt, advice]) => {
      if (!advice) return;
      const card = document.createElement("div");
      card.style.cssText = "background:#f5f4fb;border-radius:8px;padding:1rem 1.25rem;font-size:0.9rem;line-height:1.6;color:#190D62";
      const optLabel = document.createElement("strong");
      optLabel.style.cssText = "display:block;margin-bottom:0.4rem;font-size:0.8rem;text-transform:uppercase;letter-spacing:0.04em;color:#6D1E6F";
      optLabel.textContent = opt;
      card.appendChild(optLabel);
      const body = document.createElement("div");
      body.className = "s-article";
      body.innerHTML = advice;
      card.appendChild(body);
      adviceSection.appendChild(card);
    });
    bodyCol.appendChild(adviceSection);

    section.appendChild(bodyCol);

    if (q.image) {
      const imageEl = document.createElement("div");
      imageEl.className = "aq-question-image";
      imageEl.innerHTML = q.image;
      section.appendChild(imageEl);
    }

    container.appendChild(section);
  });
}

// --- Quiz screen ---

function renderQuizScreen() {
  const container = $("[data-screen='quiz'] .aq-question-area");
  if (!container) return;
  renderQuestion(container);
}

function renderQuestion(container) {
  const { idx, question: q } = GROUPED_QUESTIONS[state.currentQuestion];
  container.innerHTML = "";

  // Ensure question area is visible, feedback area hidden
  container.hidden = false;
  const feedbackArea = $(".aq-feedback-area");
  if (feedbackArea) feedbackArea.hidden = true;

  // Restore progress bar (hidden during feedback and hero screens)
  const progressEl = $(".aq-progress");
  if (progressEl) progressEl.hidden = false;

  // Show back button only from Q2 onwards
  const backBtn = $(".aq-quiz-back");
  if (backBtn) backBtn.hidden = state.currentQuestion === 0;

  const questionEl = document.createElement("div");
  questionEl.className = "aq-question";

  // Left column: text + options
  const bodyCol = document.createElement("div");
  bodyCol.className = "aq-question-body";

  const text = document.createElement("h2");
  text.className = "aq-question-text";
  text.textContent = q.text;
  bodyCol.appendChild(text);

  if (q.context) {
    const contextEl = document.createElement("p");
    contextEl.className = "aq-question-context";
    contextEl.textContent = q.context;
    bodyCol.appendChild(contextEl);
  }

  const optionsWrap = document.createElement("div");
  optionsWrap.className = "aq-options";

  const existingAnswer = state.answers[idx];

  Object.keys(q.answerAdvice).forEach((opt) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "aq-option";
    if (existingAnswer === opt) btn.classList.add("aq-option--selected");
    btn.textContent = opt;
    btn.addEventListener("click", () => selectOption(idx, opt, container));
    optionsWrap.appendChild(btn);
  });

  bodyCol.appendChild(optionsWrap);
  questionEl.appendChild(bodyCol);

  // Right column: illustration
  const img = q.image;
  if (img) {
    const imageEl = document.createElement("div");
    imageEl.className = "aq-question-image";
    imageEl.innerHTML = img;
    questionEl.appendChild(imageEl);
  }

  container.appendChild(questionEl);

  // Next button — in fixed bar so it's always visible
  const nextBtn = document.createElement("button");
  nextBtn.type = "button";
  nextBtn.className = "aq-next-btn aq-btn-primary";
  nextBtn.disabled = existingAnswer === undefined;
  nextBtn.textContent = "Next →";
  nextBtn.addEventListener("click", renderFeedbackScreen);
  const nextBar = document.createElement("div");
  nextBar.className = "aq-btn-bar";
  const nextBarInner = document.createElement("div");
  nextBarInner.className = "aq-btn-bar-inner";
  nextBarInner.appendChild(nextBtn);
  nextBar.appendChild(nextBarInner);
  container.appendChild(nextBar);

  const firstOption = container.querySelector(".aq-option");
  if (firstOption) firstOption.focus();
}

function renderFeedbackScreen() {
  const { idx, question: q } = GROUPED_QUESTIONS[state.currentQuestion];
  const value = state.answers[idx];
  const norm = normaliseAnswer(q, value);

  // Skip feedback entirely for N/A answers
  if (norm === null) {
    advanceFromQuestion();
    return;
  }

  // Hide progress bar and back button during feedback
  const progressEl = $(".aq-progress");
  if (progressEl) progressEl.hidden = true;
  const backBtn = $(".aq-quiz-back");
  if (backBtn) backBtn.hidden = true;

  const feedbackArea = $(".aq-feedback-area");
  feedbackArea.innerHTML = "";

  const wrap = document.createElement("div");
  wrap.className = "aq-feedback-screen";

  // Card wrapping the text content
  const card = document.createElement("div");
  card.className = "aq-feedback-card";

  const areaLabel = document.createElement("p");
  areaLabel.className = "aq-feedback-area-label";
  areaLabel.textContent = q.shortLabel;
  card.appendChild(areaLabel);

  const youAnswered = document.createElement("p");
  youAnswered.className = "aq-feedback-you-answered";
  youAnswered.textContent = `You answered "${value}"`;
  card.appendChild(youAnswered);

  const prose = document.createElement("p");
  prose.className = "aq-feedback-prose s-article";
  prose.innerHTML = (q.answerAdvice && q.answerAdvice[value]) || "";
  card.appendChild(prose);

  wrap.appendChild(card);
  feedbackArea.appendChild(wrap);

  // Continue button in fixed bar
  const continueBtn = document.createElement("button");
  continueBtn.type = "button";
  continueBtn.className = "aq-continue-btn aq-btn-secondary";
  continueBtn.textContent = "Continue →";
  continueBtn.addEventListener("click", advanceFromQuestion);
  const contBar = document.createElement("div");
  contBar.className = "aq-btn-bar";
  const contBarInner = document.createElement("div");
  contBarInner.className = "aq-btn-bar-inner";
  contBarInner.appendChild(continueBtn);
  contBar.appendChild(contBarInner);
  feedbackArea.appendChild(contBar);

  $(".aq-question-area").hidden = true;
  feedbackArea.hidden = false;

  scrollToQuizTop();
  continueBtn.focus();
}

function advanceFromQuestion() {
  const container = $(".aq-question-area");
  scrollToQuizTop();
  if (state.currentQuestion < GROUPED_QUESTIONS.length - 1) {
    state.currentQuestion++;
    updateProgress();
    renderQuestion(container);
  } else {
    const { score, perQuestion } = computeScore(state.answers);
    state.score = score;
    state.perQuestion = perQuestion;
    state.topTwo = deriveTopTwo(perQuestion);
    state.feedback = buildFeedback(perQuestion);
    state.groupScores = computeGroupScores(perQuestion);
    if (PREFILLED_UID) {
      state.uid = PREFILLED_UID;
      submitQuiz({
        email: "",
        uid: state.uid,
        score: state.score,
        topTwo: state.topTwo,
        source: state.source,
        groupScores: state.groupScores,
        perQuestion: state.perQuestion,
      }).catch((err) => console.error("Quiz submit error:", err));
      renderResults();
      showScreen("results");
    } else {
      showScreen("email");
    }
    updateProgress();
  }
}

function selectOption(questionIdx, value, container) {
  state.answers[questionIdx] = value;

  $$(".aq-option", container).forEach((btn) => {
    btn.classList.toggle("aq-option--selected", btn.textContent === value);
  });

  const nextBtn = container.querySelector(".aq-next-btn");
  if (nextBtn) {
    nextBtn.disabled = false;
    nextBtn.focus();
  }
}

// --- Email capture screen ---

function handleEmailSubmit(e) {
  e.preventDefault();
  const form = e.target;
  const emailInput = form.querySelector("[name='email']");
  const honeypot = form.querySelector("[name='website_url']");

  if (honeypot && honeypot.value) return;

  state.email = emailInput.value.trim();
  if (!state.email) return;

  if (window.sendMessage) window.sendMessage("Click", "Resource", "Airtable Maturity Assessment");

  submitQuiz({
    email: state.email,
    uid: "",
    score: state.score,
    topTwo: state.topTwo,
    source: state.source,
    groupScores: state.groupScores,
    perQuestion: state.perQuestion,
  }).catch((err) => console.error("Quiz submit error:", err));

  renderResults();
  showScreen("results");
}

// --- Results screen ---

function renderResults() {
  const scoreEl = $(".aq-score-number");
  if (scoreEl) scoreEl.textContent = state.score.toFixed(1);

  const maxEl = $(".aq-score-max");
  if (maxEl) maxEl.textContent = "/ 10";

  const band = overallBand(state.score);
  const bandEl = $(".aq-score-band");
  if (bandEl) {
    bandEl.textContent = band.band + " maturity";
    bandEl.dataset.band = band.band.toLowerCase();
  }

  const summaryEl = $(".aq-score-summary");
  if (summaryEl) summaryEl.textContent = band.summary;

  const categoryContainer = $(".aq-category-scores");
  if (categoryContainer) {
    categoryContainer.innerHTML = "";
    const sorted = [...state.groupScores].sort((a, b) => {
      if (a.norm === null && b.norm === null) return 0;
      if (a.norm === null) return 1;
      if (b.norm === null) return -1;
      return b.norm - a.norm;
    });
    sorted.forEach(({ label, score, norm }) => {
      const status = statusForScore(norm);
      const statusSlug = status ? status.label.toLowerCase().replace(/\s+/g, "-") : "na";
      const pct = norm !== null ? Math.round(norm * 100) : 0;

      const card = document.createElement("div");
      card.className = "aq-cat-score";
      card.dataset.status = statusSlug;

      const header = document.createElement("div");
      header.className = "aq-cat-header";
      header.innerHTML = `<span class="aq-cat-label">${label}</span><span class="aq-cat-number">${score !== null ? score + "/10" : "N/A"}</span>`;
      card.appendChild(header);

      const bar = document.createElement("div");
      bar.className = "aq-cat-bar";
      const fill = document.createElement("div");
      fill.className = "aq-cat-bar-fill";
      fill.style.width = pct + "%";
      bar.appendChild(fill);
      card.appendChild(bar);

      categoryContainer.appendChild(card);
    });
  }

  const topTwoContainer = $(".aq-top-two");
  if (topTwoContainer && state.topTwo.length > 0) {
    // Build index → category label lookup
    const idxToCategory = {};
    CATEGORY_GROUPS.forEach(({ label: cat, questionIndices }) => {
      questionIndices.forEach((idx) => { idxToCategory[idx] = cat; });
    });

    topTwoContainer.innerHTML = `<h3 class="aq-top-two-heading">Your top ${state.topTwo.length} to address</h3>`;
    state.topTwo.forEach((item, i) => {
      const q = QUESTIONS[item.idx];
      const adviceText = (q.answerAdvice && q.answerAdvice[item.answer]) || "";
      const cat = idxToCategory[item.idx] || "";
      const div = document.createElement("div");
      div.className = "aq-top-two-item";
      div.innerHTML = `<span class="aq-top-two-number">${i + 1}</span>
        <div>
          <strong>${item.shortLabel}</strong>
          ${cat ? `<span class="aq-top-two-cat">${cat}</span>` : ""}
        </div>`;
      topTwoContainer.appendChild(div);
    });
  }
}

// --- Calendly screen ---

function renderCalendly() {
  const container = $(".aq-calendly-embed");
  if (!container) return;

  const params = new URLSearchParams({
    name: state.name,
    email: state.email,
    a1: state.score,
    a2: state.topTwo.map((t) => t.shortLabel).join(", "),
  });

  container.innerHTML = "";
  const iframe = document.createElement("iframe");
  iframe.src = `https://calendly.com/tom-halloran-mass/45min?${params.toString()}`;
  iframe.className = "aq-calendly-iframe";
  iframe.setAttribute("frameborder", "0");
  container.appendChild(iframe);
}

// --- Confirmation screen ---

// (static content, no JS needed)

// --- Wire up events ---

document.addEventListener("DOMContentLoaded", () => {
  init();

  // Hero start button
  const startBtn = $(".aq-hero-start");
  if (startBtn) startBtn.addEventListener("click", () => {
    if (window.sendMessage) window.sendMessage("Click", "Resource", "Airtable Maturity Assessment");
    showScreen("quiz");
    renderQuizScreen();
    updateProgress();
    const progress = $(".aq-progress");
    if (progress) progress.hidden = false;
  });

  const emailForm = $(".aq-email-form");
  if (emailForm) emailForm.addEventListener("submit", handleEmailSubmit);

  const resultsCtaBtn = $(".aq-results-cta-button");
  if (resultsCtaBtn) resultsCtaBtn.addEventListener("click", () => {
    if (window.sendMessage) window.sendMessage("Click", "Book a call", "Airtable Maturity Assessment");
    if (window.fireConversion) window.fireConversion();
    renderCalendly();
    showScreen("calendly");
  });

  // Back buttons
  $$(".aq-back-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (state.currentScreen === "email") {
        state.currentQuestion = GROUPED_QUESTIONS.length - 1;
        showScreen("quiz");
        renderQuizScreen();
        updateProgress();
      }
    });
  });

  // Quiz back button — behaviour depends on which view is showing
  const quizBack = $(".aq-quiz-back");
  if (quizBack) {
    quizBack.addEventListener("click", () => {
      const feedbackArea = $(".aq-feedback-area");
      if (feedbackArea && !feedbackArea.hidden) {
        // On feedback screen → go back to the current question (allow answer change)
        feedbackArea.hidden = true;
        const container = $(".aq-question-area");
        container.hidden = false;
        const progressEl = $(".aq-progress");
        if (progressEl) progressEl.hidden = false;
        if (state.currentQuestion === 0) quizBack.hidden = true;
        scrollToQuizTop();
      } else {
        // On question screen → go to previous question
        if (state.currentQuestion > 0) {
          state.currentQuestion--;
          updateProgress();
          renderQuizScreen();
        }
      }
    });
  }
});
